export type AppDepartment =
  | 'cultureTalent'
  | 'sales'
  | 'services'
  | 'development'
  | 'reporting'
  | 'operations'
  | 'tools'
  | 'communication';

export const DEPARTMENT_LABELS: Record<AppDepartment, string> = {
  cultureTalent: 'Culture and Talent',
  sales: 'Sales',
  services: 'Services',
  development: 'Development',
  reporting: 'Reporting',
  operations: 'Operations',
  tools: 'Tools',
  communication: 'Chat',
};

export const DEPARTMENT_ORDER: AppDepartment[] = [
  'cultureTalent',
  'sales',
  'services',
  'development',
  'reporting',
  'operations',
  'tools',
  'communication',
];

export const DEPARTMENT_ICONS: Record<AppDepartment, string> = {
  cultureTalent: 'People',
  sales: 'Money',
  services: 'Clock',
  development: 'DeveloperTools',
  reporting: 'ReportDocument',
  operations: 'Shop',
  tools: 'Settings',
  communication: 'Comment',
};

export type ToolsSubgroup = 'connectwise' | 'security' | 'documentation';

export const TOOLS_SUBGROUP_LABELS: Record<ToolsSubgroup, string> = {
  connectwise: 'ConnectWise Administration',
  security: 'Security & Credentials',
  documentation: 'Documentation & Feedback',
};

export const TOOLS_SUBGROUP_ORDER: ToolsSubgroup[] = [
  'connectwise',
  'security',
  'documentation',
];

export const REPORT_AUTHORIZED_UPNS = [
  'wdearment@net-cloud.com',
  'tcorapi@net-cloud.com',
  'jbahm@net-cloud.com',
  'dkretz@net-cloud.com',
];

export interface AppCatalogNavChild {
  id: string;
  title: string;
  route: string;
  icon: string;
}

export interface AppCatalogEntry {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: string;
  department: AppDepartment;
  toolsSubgroup?: ToolsSubgroup;
  roles: string[];
  authorizedUpns?: string[];
  quickAccess?: boolean;
  sortOrder?: number;
  navChildren?: AppCatalogNavChild[];
  /** When true, entry is a nav group header only (no direct navigation). */
  navGroupOnly?: boolean;
  /** Optional alternate label for the nav submenu parent. */
  navTitle?: string;
}

export const APP_CATALOG: AppCatalogEntry[] = [
  // Culture and Talent
  {
    id: 'absenceRequests',
    title: 'Absence Requests',
    description: 'View and submit absence requests',
    route: '/absence-request',
    icon: 'Calendar',
    department: 'cultureTalent',
    roles: [],
    quickAccess: true,
    sortOrder: 10,
    navChildren: [
      { id: 'viewRequests', title: 'View Requests', route: '/absence-request', icon: 'View' },
      { id: 'newRequest', title: 'New Request', route: '/absence-request', icon: 'Add' },
    ],
  },
  {
    id: 'employeeForms',
    title: 'Employee Forms',
    description: 'Onboard and offboard employees',
    route: '/employee-forms',
    icon: 'ContactCard',
    department: 'cultureTalent',
    roles: ['Submit.EmployeeForm'],
    sortOrder: 20,
    navGroupOnly: true,
    navTitle: 'Employees',
    navChildren: [
      { id: 'employeeFormsList', title: 'Employee Forms', route: '/employee-forms', icon: 'ContactCard' },
      { id: 'addEmployee', title: 'Add Employee', route: '/employee-forms', icon: 'AddFriend' },
      { id: 'termEmployee', title: 'Term Employee', route: '/employee-forms', icon: 'UserRemove' },
      { id: 'updateEmployee', title: 'Update Employee', route: '/employee-update', icon: 'EditContact' },
    ],
  },
  {
    id: 'employeeUpdate',
    title: 'Employee Update Form',
    description: 'Update your personal information, address, and emergency contacts',
    route: '/employee-update',
    icon: 'EditContact',
    department: 'cultureTalent',
    roles: [],
    quickAccess: true,
    sortOrder: 30,
  },
  {
    id: 'documentAcceptance',
    title: 'Document Acceptance',
    description: 'Manage document uploads and track employee acceptance',
    route: '/acceptancemanager',
    icon: 'DocumentApproval',
    department: 'cultureTalent',
    roles: ['DocumentAcceptance.Manager', 'NtForms.Developer'],
    sortOrder: 40,
  },

  // Sales
  {
    id: 'salesDashboard',
    title: 'Sales Dashboard',
    description: 'View sales revenue, orders, and opportunities by territory',
    route: '/sales-dashboard',
    icon: 'Money',
    department: 'sales',
    roles: [],
    sortOrder: 10,
  },
  {
    id: 'salesHeatMap',
    title: 'Sales Heat Map',
    description: 'Track technology adoption and awareness across customers',
    route: '/salesheatmap',
    icon: 'WorldClock',
    department: 'sales',
    roles: ['SalesHeatMap.Write', 'SalesHeatMap.Admin', 'NtForms.Developer'],
    sortOrder: 20,
  },
  {
    id: 'renewalDashboard',
    title: 'Renewal Dashboard',
    description: 'Track upcoming agreement and configuration renewals with opportunity links',
    route: '/renewal-dashboard',
    icon: 'DateTime',
    department: 'sales',
    roles: [],
    sortOrder: 25,
  },
  {
    id: 'expiredRenewals',
    title: 'Expired Renewals',
    description: 'Active agreement and configuration renewals that have already expired',
    route: '/renewal-dashboard/expired',
    icon: 'Warning',
    department: 'sales',
    roles: [],
    sortOrder: 26,
  },
  {
    id: 'redGreen',
    title: 'Red/Green Credit Check',
    description: 'Verify opportunity amounts and credit availability',
    route: '/red-green',
    icon: 'Lightbulb',
    department: 'sales',
    roles: [],
    sortOrder: 30,
  },
  {
    id: 'opportunityAnalysis',
    title: 'Opportunity Analysis',
    description: 'Analyze won opportunities with sales cycle metrics and performance data',
    route: '/opportunityanalysis',
    icon: 'BarChartVertical',
    department: 'sales',
    roles: ['OppAnalysis.Reader', 'NtForms.Developer'],
    sortOrder: 40,
  },
  {
    id: 'hpSalesTracker',
    title: 'HP Sales Tracker Importer',
    description: 'Upload HPE Sales Tracker Excel files for reporting annual sales',
    route: '/hp-sales-tracker',
    icon: 'Upload',
    department: 'sales',
    roles: ['HPSalesTrackerImporter', 'NtForms.Developer'],
    sortOrder: 50,
  },
  {
    id: 'hpeBom',
    title: 'HPE BOM Processing',
    description: 'Calculate product line totals for HPE Bill of Materials',
    route: '/hpe-bom',
    icon: 'ExcelDocument',
    department: 'sales',
    roles: ['HpeBomProcessing.View', 'NtForms.Developer'],
    sortOrder: 60,
  },

  // Services
  {
    id: 'serviceTimeAnalysis',
    title: 'Service Time Analysis',
    description: 'Analyze service time utilization and patterns',
    route: '/service-time-analysis',
    icon: 'Clock',
    department: 'services',
    roles: ['ServiceTimeAnalysis.Viewer', 'ServiceDeliveryManager', 'NtForms.Developer'],
    sortOrder: 10,
  },
  {
    id: 'onCallManager',
    title: 'On-Call Manager',
    description: 'Manage on-call member configuration and notifications',
    route: '/oncall-manager',
    icon: 'Ringer',
    department: 'services',
    roles: [],
    quickAccess: true,
    sortOrder: 20,
  },
  {
    id: 'changeControl',
    title: 'Change Control',
    description: 'Submit and manage change control requests and approvals',
    route: '/change-control',
    icon: 'ChangeEntitlements',
    department: 'services',
    roles: [],
    quickAccess: true,
    sortOrder: 30,
  },
  {
    id: 'eolEos',
    title: 'EOL/EOS Tracking',
    description: 'Track End of Life and End of Support dates for products',
    route: '/eol-eos',
    icon: 'DateTime',
    department: 'services',
    roles: ['NtForms.Developer', 'EolEos.Read', 'EolEos.Write', 'EolEos.Import'],
    sortOrder: 40,
  },

  // Development
  {
    id: 'developmentDashboard',
    title: 'Development Dashboard',
    description: 'Weekly EOS metrics for Development board including SLA compliance and ticket stats',
    route: '/development-dashboard',
    icon: 'DeveloperTools',
    department: 'development',
    roles: ['NtForms.Developer', 'COO'],
    sortOrder: 10,
  },
  {
    id: 'backlogs',
    title: 'Backlogs',
    description: 'Manage development and service backlogs',
    route: '/backlogs',
    icon: 'BulletedList',
    department: 'development',
    roles: ['NtForms.Developer'],
    sortOrder: 20,
  },
  {
    id: 'devPriority',
    title: 'Dev Priority',
    description: 'Manage and prioritize development tickets',
    route: '/devpriority',
    icon: 'FavoriteStarFill',
    department: 'development',
    roles: ['NtForms.Developer'],
    sortOrder: 30,
  },
  {
    id: 'estimateActionPlan',
    title: 'Estimate Action Plan',
    description: 'Manage tasks and schedules for approved project estimates',
    route: '/estimate-action-plan',
    icon: 'TaskManager',
    department: 'development',
    roles: ['NtForms.Developer'],
    sortOrder: 40,
  },

  // Reporting
  {
    id: 'reports',
    title: 'Reports',
    description: 'Business intelligence reports including revenue analysis and financial data',
    route: '/reports',
    icon: 'ReportDocument',
    department: 'reporting',
    roles: ['NtForms.Developer', 'SalesDirector', 'ServiceDeliveryManager'],
    authorizedUpns: REPORT_AUTHORIZED_UPNS,
    sortOrder: 10,
  },
  {
    id: 'maintenanceSupportRevenue',
    title: 'Maintenance & Support Revenue',
    description: 'Analyze maintenance and support revenue by classification',
    route: '/reports/maintenance-support-revenue',
    icon: 'Money',
    department: 'reporting',
    roles: ['NtForms.Developer'],
    authorizedUpns: REPORT_AUTHORIZED_UPNS,
    sortOrder: 20,
  },
  {
    id: 'saMetricsReport',
    title: 'SA Metrics Report',
    description: 'Solutions Architect activity metrics (HTML report viewer)',
    route: '/reports/sa-metrics',
    icon: 'ReportDocument',
    department: 'reporting',
    roles: ['NtForms.Developer', 'SalesDirector'],
    sortOrder: 25,
  },
  {
    id: 'cwSlaTicketMemberBreakdown',
    title: 'CW SLA Ticket Member Breakdown',
    description: 'SLA acknowledgment and resolution metrics by ticket owner and board',
    route: '/reports/cw-sla-ticket-member-breakdown',
    icon: 'ReportDocument',
    department: 'reporting',
    roles: ['NtForms.Developer'],
    sortOrder: 26,
  },
  {
    id: 'servicesEosReport',
    title: 'Services EOS Report',
    description: 'Services EOS SLA by board over a date range with Tiered and special-board totals',
    route: '/reports/services-eos',
    icon: 'ReportDocument',
    department: 'reporting',
    roles: ['NtForms.Developer', 'ServiceDeliveryManager'],
    sortOrder: 27,
  },
  {
    id: 'cwUserTimeSheetSummary',
    title: 'CW User Time Sheet Summary',
    description: 'Weekly approved time sheet hours by employee (regular, OT, PTO, leave)',
    route: '/reports/cw-user-time-sheet-summary',
    icon: 'ReportDocument',
    department: 'reporting',
    roles: ['NtForms.Developer', 'AcctAdmin', 'COO'],
    sortOrder: 28,
  },
  {
    id: 'cwUserTimeSheetSummaryExport',
    title: 'CW User Time Sheet Summary For Export',
    description: 'Payroll export view of approved time sheet hours aggregated by employee',
    route: '/reports/cw-user-time-sheet-summary-export',
    icon: 'ReportDocument',
    department: 'reporting',
    roles: ['NtForms.Developer', 'AcctAdmin', 'COO'],
    sortOrder: 29,
  },
  {
    id: 'experianExport',
    title: 'Experian Export',
    description: 'Export invoiced companies and full Experian files',
    route: '/experianexport',
    icon: 'ExcelDocument',
    department: 'reporting',
    roles: ['Experian.Export', 'NtForms.Developer'],
    sortOrder: 30,
  },
  {
    id: 'ntgoalDashboard',
    title: 'Nt Goal Dashboard',
    description: 'Track financial goals and ConnectWise opportunities',
    route: '/ntgoal-dashboard',
    icon: 'BarChartVertical',
    department: 'reporting',
    roles: ['NtForms.Developer', 'NettechGoals.Viewer'],
    sortOrder: 40,
  },
  {
    id: 'clientInformation',
    title: 'Client Information',
    description: 'Generate client PDFs and update ConnectWise company data from finance forms',
    route: '/clientinformation',
    icon: 'ContactCard',
    department: 'reporting',
    roles: ['NtForms.Developer'],
    sortOrder: 50,
  },

  // Operations
  {
    id: 'inventory',
    title: 'Inventory Management',
    description: 'Track and manage company assets, equipment, and inventory',
    route: '/inventory',
    icon: 'Database',
    department: 'operations',
    roles: ['NtForms.Developer', 'inventory.readonly', 'inventory.manager', 'inventory.admin'],
    sortOrder: 10,
  },
  {
    id: 'purchaseRequest',
    title: 'Purchase Request',
    description: 'Submit purchase requests for manager approval',
    route: '/purchase-request',
    icon: 'ShoppingCart',
    department: 'operations',
    roles: ['NtForms.Developer'],
    sortOrder: 20,
  },
  {
    id: 'usacImport',
    title: 'USAC Import',
    description: 'Import and manage E-rate FRN data',
    route: '/usac-import',
    icon: 'Upload',
    department: 'operations',
    roles: ['USAC.User'],
    sortOrder: 30,
  },
  {
    id: 'platformMapping',
    title: 'Platform Mapping',
    description: 'Map Auvik, Datto, and SentinelOne sites to ConnectWise companies',
    route: '/platform-mapping',
    icon: 'Link',
    department: 'services',
    roles: ['AuvikMapper', 'SentinelMapper', 'NtForms.Developer'],
    sortOrder: 50,
  },
  {
    id: 'companyTeamCleanup',
    title: 'Company Team Cleanup',
    description: 'Remove members from ConnectWise company teams in bulk',
    route: '/company-team-cleanup',
    icon: 'PeopleBlock',
    department: 'operations',
    roles: ['CompanyTeamCleanup', 'NtForms.Developer'],
    sortOrder: 50,
  },

  // Tools
  {
    id: 'connectwiseTools',
    title: 'ConnectWise Tools',
    description: 'Administrative tools for ConnectWise data management',
    route: '/cwtools',
    icon: 'ConnectContacts',
    department: 'tools',
    toolsSubgroup: 'connectwise',
    roles: ['CwTools.View', 'NtForms.Developer'],
    sortOrder: 10,
  },
  {
    id: 'keyManagement',
    title: 'Key Management',
    description: 'Manage application secrets in Azure Key Vault',
    route: '/admin/key-management/secrets',
    icon: 'Lock',
    department: 'tools',
    toolsSubgroup: 'security',
    roles: ['KeyVault.Admin', 'NtForms.Developer'],
    sortOrder: 20,
  },
  {
    id: 'keyVaultPermissions',
    title: 'Key Vault Permissions',
    description: 'Manage Key Vault access permissions',
    route: '/admin/key-management/permissions',
    icon: 'Permissions',
    department: 'tools',
    toolsSubgroup: 'security',
    roles: ['KeyVault.Admin', 'NtForms.Developer'],
    sortOrder: 30,
  },
  {
    id: 'ntPass',
    title: 'NtPass Password Manager',
    description: 'Internal password management utility',
    route: '/ntpass',
    icon: 'Lock',
    department: 'tools',
    toolsSubgroup: 'security',
    roles: ['NtForms.Developer'],
    sortOrder: 40,
  },
  {
    id: 'reportDocs',
    title: 'Reports Documentation',
    description: 'Report Server catalog with subscriptions and schedules',
    route: '/docs',
    icon: 'ReportDocument',
    department: 'tools',
    toolsSubgroup: 'documentation',
    roles: ['DevDocs.View', 'NtForms.Developer'],
    sortOrder: 50,
  },
  {
    id: 'reportSurvey',
    title: 'Report Usage Survey',
    description: 'Help prioritize report migrations by indicating which reports you use',
    route: '/report-survey',
    icon: 'Questionnaire',
    department: 'tools',
    toolsSubgroup: 'documentation',
    roles: [],
    sortOrder: 60,
  },
  {
    id: 'reportSurveyAdmin',
    title: 'Survey Administration',
    description: 'Administer report usage survey responses',
    route: '/report-survey-admin',
    icon: 'Admin',
    department: 'tools',
    toolsSubgroup: 'documentation',
    roles: ['NtForms.Developer'],
    sortOrder: 70,
  },

  // Communication
  {
    id: 'lokiAi',
    title: 'Loki AI Chat',
    description: 'Conversational AI assistant with customizable personalities',
    route: '/loki',
    icon: 'ChatBot',
    department: 'communication',
    roles: [],
    quickAccess: true,
    sortOrder: 10,
  },
  {
    id: 'nettechChat',
    title: 'Nettech Chat',
    description: 'Shared internal chat for all authenticated users with 12-hour in-memory history',
    route: '/chat',
    icon: 'Comment',
    department: 'communication',
    roles: [],
    quickAccess: true,
    sortOrder: 20,
  },
];
